/**
 * Shared credit pricing constants and burn rate calculation logic.
 * Used by CampaignBurnRate component and CSVCampaignForm.
 */

/** Credit cost per lead action (1 credit = 1 EUR) */
export const CREDIT_COST = {
  /** Lead generation */
  GENERATE: 0.45,
  /** Enrichment & ICP — always applied to every lead */
  ENRICH: 5,
  /** Outreach module: multichannel text outreach (includes all standard channels) */
  OUTREACH: 15,
  /** Voice addon: phone/call channel surcharge — only when explicitly opted in */
  VOICE_ADDON: 10,
  /** Follow-up and conversion */
  CONVERT: 50,
} as const;

/**
 * Calculate the per-lead credit cost for a campaign.
 * Enrichment is always included. Outreach added when channels exist & not blocked.
 * Voice addon only applies when explicitly opted in (not auto-detected from channels).
 */
export const calculateCostPerLead = (params: {
  channelLabels: string[];
  outreachBlocked: boolean;
  /** Explicit voice addon — not auto-detected from channel labels */
  includeVoiceAddon?: boolean;
}): number => {
  const { channelLabels, outreachBlocked, includeVoiceAddon } = params;
  let cost = CREDIT_COST.ENRICH;

  const hasOutreach = channelLabels.length > 0 && !outreachBlocked;
  if (hasOutreach) {
    cost += CREDIT_COST.OUTREACH;
    if (includeVoiceAddon) {
      cost += CREDIT_COST.VOICE_ADDON;
    }
  }

  return cost;
};

/** Calculate weekly credit burn. Returns null if no weekly_limit set. */
export const calculateWeeklyBurn = (params: {
  weeklyLimit: number | null | undefined;
  costPerLead: number;
}): number | null => {
  const limit = params.weeklyLimit;
  if (!limit || limit <= 0) return null;
  return limit * params.costPerLead;
};

/** Calculate estimated weeks of credits remaining. Returns null if weeklyBurn is null. */
export const calculateWeeksRemaining = (params: {
  availableCredits: number;
  weeklyBurn: number | null;
}): number | null => {
  if (params.weeklyBurn === null || params.weeklyBurn <= 0) return null;
  return Math.floor(params.availableCredits / params.weeklyBurn);
};

/** Full burn rate result for a single campaign */
export interface BurnRateResult {
  costPerLead: number;
  weeklyBurn: number | null;
  weeksRemaining: number | null;
  hasOutreach: boolean;
}

/** One-call convenience: compute the full burn rate for a campaign */
export const getCampaignBurnRate = (params: {
  channelLabels: string[];
  outreachBlocked: boolean;
  weeklyLimit: number | null | undefined;
  availableCredits: number;
  /** Explicit voice addon — not auto-detected from channel labels */
  includeVoiceAddon?: boolean;
}): BurnRateResult => {
  const { channelLabels, outreachBlocked, weeklyLimit, availableCredits, includeVoiceAddon } = params;
  const hasOutreach = channelLabels.length > 0 && !outreachBlocked;
  const costPerLead = calculateCostPerLead({ channelLabels, outreachBlocked, includeVoiceAddon });
  const weeklyBurn = calculateWeeklyBurn({ weeklyLimit, costPerLead });
  const weeksRemaining = calculateWeeksRemaining({ availableCredits, weeklyBurn });

  return { costPerLead, weeklyBurn, weeksRemaining, hasOutreach };
};

export interface CampaignInvestmentResult {
  generatedLeadCount: number;
  generatedCost: number;
  enrichedLeadCount: number;
  enrichedCost: number;
  outreachLeadCount: number;
  outreachUnitCost: number;
  outreachCost: number;
  convertedLeadCount: number;
  convertedCost: number;
  totalCredits: number;
  hasVoice: boolean;
  leadGenerationApplied: boolean;
}

export const calculateCampaignInvestment = (params: {
  generatedLeadCount: number;
  leadGenerationApplied: boolean;
  enrichedLeadCount: number;
  outreachLeadCount: number;
  convertedLeadCount: number;
  /** Explicit voice addon — not auto-detected from channel labels */
  includeVoiceAddon?: boolean;
}): CampaignInvestmentResult => {
  const hasVoice = Boolean(params.includeVoiceAddon);
  const outreachUnitCost = CREDIT_COST.OUTREACH + (hasVoice ? CREDIT_COST.VOICE_ADDON : 0);
  const generatedLeadCount = params.leadGenerationApplied ? params.generatedLeadCount : 0;
  const generatedCost = generatedLeadCount * CREDIT_COST.GENERATE;
  const enrichedCost = params.enrichedLeadCount * CREDIT_COST.ENRICH;
  const outreachCost = params.outreachLeadCount * outreachUnitCost;
  const convertedCost = params.convertedLeadCount * CREDIT_COST.CONVERT;

  return {
    generatedLeadCount,
    generatedCost,
    enrichedLeadCount: params.enrichedLeadCount,
    enrichedCost,
    outreachLeadCount: params.outreachLeadCount,
    outreachUnitCost,
    outreachCost,
    convertedLeadCount: params.convertedLeadCount,
    convertedCost,
    totalCredits: generatedCost + enrichedCost + outreachCost + convertedCost,
    hasVoice,
    leadGenerationApplied: params.leadGenerationApplied,
  };
};

/**
 * The INVESTED display rows (Manuel): exactly three categories — Generated,
 * Enriched, Outreached — so the visible rows always sum to the shown total.
 * OUTREACHED reads 0 unless the team holds the SALES INTELLIGENCE module:
 * outreach spend only accrues on sales teams (or when humans move lead
 * statuses manually in the ISR view — those manual moves still count, which
 * is why a no-sales team CAN show outreached leads with 0 credited cost...
 * no: per Manuel the category reads 0 for no-sales teams, full stop).
 * Converted is intentionally NOT a row: converted leads' enrich + outreach
 * costs are already inside the Enriched/Outreached buckets, and a 4th row
 * that users can't reconcile was the original confusion.
 */
export interface CampaignInvestmentRow {
  key: 'generated' | 'enriched' | 'outreach';
  label: string;
  shortLabel: string;
  count: number;
  unit: number;
  total: number;
  tone: string;
}

export const buildCampaignInvestmentRows = (
  investment: CampaignInvestmentResult,
  options: { includeOutreach: boolean },
): CampaignInvestmentRow[] => {
  const outreachCount = options.includeOutreach ? investment.outreachLeadCount : 0;
  const outreachUnit = options.includeOutreach ? investment.outreachUnitCost : 0;
  const outreachTotal = options.includeOutreach ? investment.outreachCost : 0;
  return [
    { key: 'generated', label: 'Generated', shortLabel: 'G', count: investment.generatedLeadCount, unit: CREDIT_COST.GENERATE, total: investment.generatedCost, tone: 'text-slate-200' },
    { key: 'enriched', label: 'Enriched', shortLabel: 'E', count: investment.enrichedLeadCount, unit: CREDIT_COST.ENRICH, total: investment.enrichedCost, tone: 'text-blue-300' },
    { key: 'outreach', label: 'Outreached', shortLabel: 'O', count: outreachCount, unit: outreachUnit, total: outreachTotal, tone: 'text-orange-300' },
  ];
};

/** Sum of the three DISPLAYED categories — the number the UI presents as total. */
export const getInvestmentRowsTotal = (rows: CampaignInvestmentRow[]): number =>
  rows.reduce((sum, row) => sum + row.total, 0);
